import React, { useEffect, useMemo, useRef, useState } from "react";
import { rootUrl } from "../../helpers";
import {
  FaSpinner,
  FaSyncAlt,
  FaMapMarkedAlt,
  FaSearch,
  FaTimes,
  FaLayerGroup,
} from "react-icons/fa";

const DEV_STATUS_OPTIONS = [
  "",
  "Complete",
  "Incomplete",
  "Undeveloped",
  "DEVELOPED",
  "Fully Developed",
];

const ZONAL_USE_OPTIONS = ["", "Residential", "Commercial", "Wall Fence"];

function loadLeafletOnce() {
  return new Promise((resolve, reject) => {
    if (window.L) return resolve(window.L);

    const existingCss = document.querySelector("link[data-leaflet-gis]");
    const existingJs = document.querySelector("script[data-leaflet-gis]");

    const onReady = () => {
      if (window.L) resolve(window.L);
      else reject(new Error("Leaflet failed to load"));
    };

    if (!existingCss) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      link.setAttribute("data-leaflet-gis", "true");
      document.head.appendChild(link);
    }

    if (!existingJs) {
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
      script.async = true;
      script.defer = true;
      script.setAttribute("data-leaflet-gis", "true");
      script.onload = onReady;
      script.onerror = reject;
      document.body.appendChild(script);
    } else {
      // If script already exists but L isn’t ready yet, poll briefly.
      const started = Date.now();
      const t = setInterval(() => {
        if (window.L) {
          clearInterval(t);
          resolve(window.L);
        } else if (Date.now() - started > 8000) {
          clearInterval(t);
          reject(new Error("Leaflet load timeout"));
        }
      }, 50);
    }
  });
}

function buildBboxFromLeafletBounds(bounds) {
  // west,south,east,north
  return `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
}

function isInRange(n, min, max) {
  return typeof n === "number" && n >= min && n <= max;
}

function swapLonLatCoord(coord) {
  // coord = [x, y] => [y, x]
  return [coord[1], coord[0]];
}

function normalizeGeometryToBbox(geometry, bboxArr) {
  // bboxArr = [xmin, ymin, xmax, ymax] where x=lon, y=lat
  if (!geometry || !bboxArr || bboxArr.length !== 4) return geometry;
  const [xmin, ymin, xmax, ymax] = bboxArr.map(Number);
  if (![xmin, ymin, xmax, ymax].every((v) => Number.isFinite(v))) return geometry;

  const coordLooksCorrect = (coord) => {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const x = Number(coord[0]);
    const y = Number(coord[1]);
    return isInRange(x, xmin, xmax) && isInRange(y, ymin, ymax);
  };

  const coordLooksSwapped = (coord) => {
    if (!Array.isArray(coord) || coord.length < 2) return false;
    const x = Number(coord[0]);
    const y = Number(coord[1]);
    return isInRange(x, ymin, ymax) && isInRange(y, xmin, xmax);
  };

  const isSaneLonLat = (lon, lat) =>
    Number.isFinite(lon) && Number.isFinite(lat) && Math.abs(lon) <= 180 && Math.abs(lat) <= 90;

  const firstCoordsSample = (coords, depth, out, limit = 40) => {
    if (!Array.isArray(coords) || out.length >= limit) return;
    if (depth === 0) {
      out.push(coords);
      return;
    }
    for (const c of coords) {
      if (out.length >= limit) break;
      firstCoordsSample(c, depth - 1, out, limit);
    }
  };

  const decideSwapForGeometry = (coords, depth) => {
    const sample = [];
    firstCoordsSample(coords, depth, sample);
    if (!sample.length) return false;
    let correct = 0;
    let swapped = 0;
    for (const c of sample) {
      if (coordLooksCorrect(c)) correct++;
      else if (coordLooksSwapped(c)) swapped++;
    }
    // If swapped signals dominate, swap entire geometry uniformly.
    return swapped > correct;
  };

  const mapCoords = (coords, depth, swapAll) => {
    if (!Array.isArray(coords)) return coords;
    if (depth === 0) {
      // coords is a single [x,y] point
      const rawX = Number(coords[0]);
      const rawY = Number(coords[1]);
      const [lon, lat] = swapAll ? [rawY, rawX] : [rawX, rawY];
      // Drop obviously broken points (prevents "spike to nowhere")
      if (!isSaneLonLat(lon, lat)) return null;
      return [lon, lat];
    }
    return coords
      .map((c) => mapCoords(c, depth - 1, swapAll))
      .filter((v) => v !== null);
  };

  // Polygon coords depth: 2 (rings -> points)
  // MultiPolygon coords depth: 3 (polys -> rings -> points)
  if (geometry.type === "Polygon") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 2);
    const coords = mapCoords(geometry.coordinates, 2, swapAll);
    // Ensure rings are closed
    const closed = (coords || []).map((ring) => {
      if (!Array.isArray(ring) || ring.length < 3) return ring;
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first && last && first[0] === last[0] && first[1] === last[1]) return ring;
      return [...ring, first];
    });
    return { ...geometry, coordinates: closed };
  }
  if (geometry.type === "MultiPolygon") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 3);
    const coords = mapCoords(geometry.coordinates, 3, swapAll);
    const closed = (coords || []).map((poly) =>
      (poly || []).map((ring) => {
        if (!Array.isArray(ring) || ring.length < 3) return ring;
        const first = ring[0];
        const last = ring[ring.length - 1];
        if (first && last && first[0] === last[0] && first[1] === last[1]) return ring;
        return [...ring, first];
      })
    );
    return { ...geometry, coordinates: closed };
  }
  if (geometry.type === "LineString") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 1);
    return { ...geometry, coordinates: mapCoords(geometry.coordinates, 1, swapAll) };
  }
  if (geometry.type === "MultiLineString") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 2);
    return { ...geometry, coordinates: mapCoords(geometry.coordinates, 2, swapAll) };
  }
  if (geometry.type === "Point") {
    const swapAll = decideSwapForGeometry([geometry.coordinates], 1);
    const mapped = mapCoords(geometry.coordinates, 0, swapAll);
    return mapped ? { ...geometry, coordinates: mapped } : geometry;
  }
  if (geometry.type === "MultiPoint") {
    const swapAll = decideSwapForGeometry(geometry.coordinates, 1);
    return { ...geometry, coordinates: mapCoords(geometry.coordinates, 1, swapAll) };
  }
  return geometry;
}

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

export default function LandManagement() {
  const mapElRef = useRef(null);
  const mapRef = useRef(null);
  const layerControlRef = useRef(null);
  const plotsLayerRef = useRef(null);
  const lastReqAbortRef = useRef(null);
  const cacheRef = useRef(new Map());

  const [leafletReady, setLeafletReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [geojson, setGeojson] = useState(null);
  const [selected, setSelected] = useState(null);

  const [filters, setFilters] = useState({
    dev_status: "",
    zonal_use: "",
    limit: 500,
    search: "",
  });

  const filteredFeatures = useMemo(() => {
    const feats = geojson?.features || [];
    const s = filters.search.trim().toLowerCase();
    if (!s) return feats;
    return feats.filter((f) => {
      const p = f?.properties || {};
      return (
        String(p.plot_num || "").toLowerCase().includes(s) ||
        String(p.city || "").toLowerCase().includes(s) ||
        String(p.location || "").toLowerCase().includes(s) ||
        String(p.dev_status || "").toLowerCase().includes(s) ||
        String(p.zonal_use || "").toLowerCase().includes(s)
      );
    });
  }, [geojson, filters.search]);

  const fetchPlots = async ({ bbox, dev_status, zonal_use, limit }) => {
    setErr("");
    const key = JSON.stringify({ bbox, dev_status, zonal_use, limit });
    if (cacheRef.current.has(key)) {
      setGeojson(cacheRef.current.get(key));
      return;
    }

    if (lastReqAbortRef.current) lastReqAbortRef.current.abort();
    const controller = new AbortController();
    lastReqAbortRef.current = controller;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("bbox", bbox);
      if (dev_status) params.set("dev_status", dev_status);
      if (zonal_use) params.set("zonal_use", zonal_use);
      if (limit) params.set("limit", String(limit));

      const res = await fetch(rootUrl(`/plots?${params.toString()}`), {
        signal: controller.signal,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || "Failed to load plots");
      }
      cacheRef.current.set(key, data);
      setGeojson(data);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setErr(e?.message || "Failed to load plots");
      setGeojson(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshFromMap = useMemo(
    () =>
      debounce(() => {
        const map = mapRef.current;
        if (!map) return;
        const bbox = buildBboxFromLeafletBounds(map.getBounds());
        fetchPlots({
          bbox,
          dev_status: filters.dev_status,
          zonal_use: filters.zonal_use,
          limit: filters.limit,
        });
      }, 350),
    [filters.dev_status, filters.zonal_use, filters.limit]
  );

  useEffect(() => {
    let mounted = true;
    loadLeafletOnce()
      .then(() => mounted && setLeafletReady(true))
      .catch((e) => mounted && setErr(e?.message || "Failed to load map"));
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!leafletReady) return;
    if (!mapElRef.current) return;
    if (mapRef.current) return;

    const L = window.L;

    const map = L.map(mapElRef.current, {
      zoomControl: true,
    }).setView([-15.4167, 28.2833], 12); // Lusaka default

    const baseLayers = {
      "OpenStreetMap": L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 20,
        attribution: "&copy; OpenStreetMap",
      }),
      "Satellite": L.tileLayer(
        "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
        { maxZoom: 20, attribution: "Tiles &copy; Esri" }
      ),
      "Carto Light": L.tileLayer("https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png", {
        maxZoom: 20,
        attribution: "&copy; Carto",
      }),
      "Carto Dark": L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
        maxZoom: 20,
        attribution: "&copy; Carto",
      }),
    };

    baseLayers["Carto Light"].addTo(map);

    const plotsRenderer = L.svg({ padding: 0.5 });

    const plotsLayer = L.geoJSON([], {
      renderer: plotsRenderer,
      style: (feature) => {
        const p = feature?.properties || {};
        const status = String(p.dev_status || "").toLowerCase();
        const color =
          status.includes("complete") || status.includes("fully")
            ? "#16a34a"
            : status.includes("incomplete")
              ? "#f59e0b"
              : status.includes("undeveloped")
                ? "#2563eb"
                : "#64748b";
        // Make polygons very visible on any basemap.
        return {
          color: "#111827", // strong outline (near-black)
          weight: 3,
          opacity: 0.95,
          fillColor: color,
          fillOpacity: 0.55,
          lineJoin: "round",
          lineCap: "round",
        };
      },
      onEachFeature: (feature, layer) => {
        layer.on("click", () => setSelected(feature));
        layer.on("mouseover", () => {
          layer.setStyle({
            weight: 5,
            color: "#ffffff",
            opacity: 1,
            fillOpacity: 0.7,
            lineJoin: "round",
            lineCap: "round",
          });
          if (layer.bringToFront) layer.bringToFront();
        });
        layer.on("mouseout", () => {
          plotsLayer.resetStyle(layer);
        });
        const p = feature?.properties || {};
        const title = `${p.plot_num || "Plot"}${p.location ? ` • ${p.location}` : ""}`;
        layer.bindTooltip(title, { sticky: true });
      },
    }).addTo(map);

    plotsLayerRef.current = plotsLayer;
    mapRef.current = map;

    const lc = L.control.layers(baseLayers, { Plots: plotsLayer }, { collapsed: true });
    lc.addTo(map);
    layerControlRef.current = lc;

    map.on("moveend zoomend", refreshFromMap);

    // initial load
    refreshFromMap();

    return () => {
      map.off("moveend zoomend", refreshFromMap);
      map.remove();
      mapRef.current = null;
      plotsLayerRef.current = null;
      layerControlRef.current = null;
    };
  }, [leafletReady, refreshFromMap]);

  useEffect(() => {
    const L = window.L;
    const layer = plotsLayerRef.current;
    if (!L || !layer) return;
    layer.clearLayers();
    if (!geojson?.features?.length) return;
    const bboxArr = Array.isArray(geojson?.bbox) ? geojson.bbox : null;
    const normalized = {
      ...geojson,
      features: geojson.features.map((f) => ({
        ...f,
        geometry: normalizeGeometryToBbox(f.geometry, bboxArr),
      })),
    };
    layer.addData(normalized);
  }, [geojson]);

  const onClearFilters = () => {
    setFilters({ dev_status: "", zonal_use: "", limit: 500, search: "" });
    cacheRef.current.clear();
    setSelected(null);
    setGeojson(null);
    setErr("");
    setTimeout(() => refreshFromMap(), 0);
  };

  const selectedProps = selected?.properties || null;

  return (
    <div className="w-full h-full flex flex-col">
      <div className="px-4 pt-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center">
              <FaMapMarkedAlt className="text-blue-700" />
            </div>
            <div>
              <div className="text-xl font-semibold text-gray-900">Land Management (GIS)</div>
              <div className="text-sm text-gray-500">
                Loads plots by current map bounds (bbox) with optional filters.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => refreshFromMap()}
              className="px-3 py-2 rounded-lg bg-gray-900 text-white text-sm flex items-center gap-2 hover:bg-gray-800"
              type="button"
            >
              <FaSyncAlt />
              Refresh
            </button>
            <button
              onClick={onClearFilters}
              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-900 text-sm flex items-center gap-2 hover:bg-gray-200"
              type="button"
            >
              <FaTimes />
              Reset
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 min-h-0">
        <div className="lg:col-span-4 xl:col-span-3 flex flex-col gap-4 min-h-0">
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-gray-900 flex items-center gap-2">
                <FaLayerGroup className="text-gray-700" />
                Filters
              </div>
              <div className="text-xs text-gray-500">Server-side bbox + filters</div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <label className="text-sm">
                <div className="text-gray-700 mb-1">Development status</div>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                  value={filters.dev_status}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, dev_status: e.target.value }));
                    cacheRef.current.clear();
                    setTimeout(() => refreshFromMap(), 0);
                  }}
                >
                  {DEV_STATUS_OPTIONS.map((v) => (
                    <option key={v || "__any"} value={v}>
                      {v ? v : "Any"}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                <div className="text-gray-700 mb-1">Zonal use</div>
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                  value={filters.zonal_use}
                  onChange={(e) => {
                    setFilters((p) => ({ ...p, zonal_use: e.target.value }));
                    cacheRef.current.clear();
                    setTimeout(() => refreshFromMap(), 0);
                  }}
                >
                  {ZONAL_USE_OPTIONS.map((v) => (
                    <option key={v || "__any"} value={v}>
                      {v ? v : "Any"}
                    </option>
                  ))}
                </select>
              </label>

              <label className="text-sm">
                <div className="text-gray-700 mb-1">Limit</div>
                <input
                  type="number"
                  min={1}
                  max={2000}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                  value={filters.limit}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(2000, Number(e.target.value || 500)));
                    setFilters((p) => ({ ...p, limit: v }));
                    cacheRef.current.clear();
                    setTimeout(() => refreshFromMap(), 0);
                  }}
                />
                <div className="text-xs text-gray-500 mt-1">Max 2000 (backend clamps).</div>
              </label>

              <label className="text-sm">
                <div className="text-gray-700 mb-1 flex items-center gap-2">
                  <FaSearch className="text-gray-500" />
                  Search (client-side)
                </div>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white"
                  value={filters.search}
                  placeholder="Plot #, city, location…"
                  onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))}
                />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="text-gray-600">
                <span className="font-semibold text-gray-900">{filteredFeatures.length}</span> visible
                {geojson?.count != null ? (
                  <span className="text-gray-400"> • {geojson.count} loaded</span>
                ) : null}
              </div>
              {loading ? (
                <div className="flex items-center gap-2 text-blue-700">
                  <FaSpinner className="animate-spin" />
                  Loading…
                </div>
              ) : null}
            </div>

            {err ? (
              <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                {err}
              </div>
            ) : null}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-4 flex-1 min-h-0 overflow-auto">
            <div className="font-semibold text-gray-900 mb-2">Selected plot</div>
            {!selectedProps ? (
              <div className="text-sm text-gray-500">Click a plot polygon on the map to view details.</div>
            ) : (
              <div className="text-sm">
                <div className="text-lg font-semibold text-gray-900">
                  {selectedProps.plot_num || "Plot"}
                </div>
                <div className="text-gray-600">{selectedProps.location || selectedProps.city || ""}</div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-500">Status</div>
                    <div className="font-medium text-gray-900">{selectedProps.dev_status || "—"}</div>
                  </div>
                  <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-500">Zonal use</div>
                    <div className="font-medium text-gray-900">{selectedProps.zonal_use || "—"}</div>
                  </div>
                  <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-500">Structures</div>
                    <div className="font-medium text-gray-900">
                      {selectedProps.num_struct != null ? selectedProps.num_struct : "—"}
                    </div>
                  </div>
                  <div className="p-2 rounded-lg bg-gray-50 border border-gray-200">
                    <div className="text-xs text-gray-500">Survey date</div>
                    <div className="font-medium text-gray-900">{selectedProps.survey_date || "—"}</div>
                  </div>
                </div>

                <button
                  type="button"
                  className="mt-4 w-full px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => {
                    const map = mapRef.current;
                    if (!map) return;
                    const cLat = selectedProps.centroid_lat;
                    const cLon = selectedProps.centroid_lon;
                    if (typeof cLat === "number" && typeof cLon === "number") {
                      map.flyTo([cLat, cLon], Math.max(map.getZoom(), 16), { duration: 0.6 });
                    }
                  }}
                >
                  Zoom to centroid
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-8 xl:col-span-9 min-h-0">
          <div className="bg-white rounded-2xl border border-gray-200 h-full overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div className="font-semibold text-gray-900">Map</div>
              <div className="text-xs text-gray-500">
                {leafletReady ? "Leaflet ready" : "Loading Leaflet…"}
              </div>
            </div>
            <div className="h-full">
              <div ref={mapElRef} className="w-full h-[70vh] lg:h-full" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

